import { Component, inject, OnInit } from '@angular/core';

import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { CommonDialogWrapperComponent } from '../common-dialog-wrapper/common-dialog-wrapper.component';
import {
  ButtonComponent,
  CheckboxComponent,
  LoaderModule,
  TabComponent
} from '@mds/ui';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { AuthApiService } from '@services/auth-api.service';
import { DataSourceApiService } from '@services/data-source-api.service';
import { UserProfileApiService } from '@services/user-profile-api.service';
import { ToastrService } from '@services/toaster.service';
import { ConfirmedValidator } from '@utils/must-match.validator';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { PerfectScrollbarModule } from 'perfect-scrollbar-angular';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  CommonCloseDialogType,
  CommonDialogButton,
  CommonHeaderIconModel
} from '../common-dialog-wrapper/common-dialog-wrapper.model';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { ColorAliases } from '@mds/common';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

export enum AccountSections {
  CHANGE_PASSWORD = 'Password',
  CONNECTED_APPS = 'Apps',
  NEWSLETTER_SETTINGS = 'Newsletter',
  DELETE_ACCOUNT = 'Account'
}

export function strongPasswordValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || '';

    const errors: ValidationErrors = {};

    if (value.length < 12) {
      errors.minLength = 'The password must be at least 12 characters long.';
    }

    if (!/[A-Z]/.test(value)) {
      errors.uppercase = 'The password requires at least one uppercase letter.';
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      errors.symbol = 'The password requires at least one symbol.';
    }

    return Object.keys(errors).length ? errors : null;
  };
}

@Component({
  selector: 'app-account-settings',
  standalone: true,
  imports: [
    MatDialogModule,
    ButtonComponent,
    MatIconModule,
    MatFormFieldModule,
    FormsModule,
    CommonDialogWrapperComponent,
    PerfectScrollbarModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MdsIconsModule,
    TabComponent,
    MatInputModule,
    CheckboxComponent,
    LoaderModule,
    MatTooltipModule
],
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss']
})
export class AccountSettingsComponent implements OnInit {
  headerText = 'Account Settings';
  dialog = inject(MatDialog);
  private dataSourceApiService = inject(DataSourceApiService);
  private userProfileApiService = inject(UserProfileApiService);
  private authApiService = inject(AuthApiService);
  data = inject(MAT_DIALOG_DATA);
  toastr = inject(ToastrService);
  readonly dialogRef = inject(MatDialogRef<AccountSettingsComponent>);
  fb = inject(UntypedFormBuilder);

  passwordTooltip =
    'Password must be at least 12 characters long and include one uppercase letter and one special character (e.g. !@#$%^&*).';
  confirmPasswordTooltip = 'Repeat the new password for security reasons. It must match the new password above';

  accountSections = AccountSections;

  accountUpdateButton: CommonDialogButton = {
    text: 'Update Password',
    type: 'primary',
    disabled: true
  };

  newsletterButton: CommonDialogButton = {
    text: 'Confirm Subscription',
    type: 'primary'
  };

  deleteAccountButton: CommonDialogButton = {
    text: 'Delete account...',
    type: 'warn'
  };

  confirmButton = null;

  accountTabs = Object.keys(this.accountSections).map((item) => {
    return { label: this.accountSections[item] };
  });

  headerIcon: CommonHeaderIconModel = {
    icon: MdsIcon.ACCOUNT_SETTINGS,
    height: 28,
    width: 28,
    pathColors: [ColorAliases.SECONDARY]
  };

  onConfirmAndCloseDialog(value?: CommonCloseDialogType) {
    if (value === CommonCloseDialogType.CONFIRM) {
      switch (this.selectedSection) {
        case AccountSections.CHANGE_PASSWORD:
          this.submit();
          break;
        case AccountSections.NEWSLETTER_SETTINGS:
          this.saveSubscription();
          break;
        case AccountSections.DELETE_ACCOUNT:
          this.deleteAccount();
          break;
        default:
          break;
      }
    } else if (value === CommonCloseDialogType.TOP) {
      this.dialogRef.close();
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  public userInfo: any;
  public userEmail = '';
  public loadingFlag = false;

  public sectionList = [Object.keys(AccountSections)];
  public selectedSection = AccountSections.CHANGE_PASSWORD;

  public subscription: any;

  form: UntypedFormGroup = new UntypedFormGroup({});
  public connectedApps: any[] = [];

  constructor() {
    this.form = this.fb.group(
      {
        password: ['', [Validators.required, strongPasswordValidator()]],
        password2: ['', [Validators.required]],
        old_password: ['', [Validators.required]]
      },
      {
        validator: ConfirmedValidator('password', 'password2')
      }
    );
  }

  getConfirmButton(): void {
    switch (this.selectedSection) {
      case AccountSections.CHANGE_PASSWORD:
        this.confirmButton = this.accountUpdateButton;
        break;
      case AccountSections.NEWSLETTER_SETTINGS:
        this.confirmButton = this.newsletterButton;
        break;
      case AccountSections.DELETE_ACCOUNT:
        this.confirmButton = this.deleteAccountButton;
        break;
      default:
        this.confirmButton = null;
        break;
    }
  }

  ngOnInit(): void {
    this.userInfo = JSON.parse(localStorage.getItem('user_info'));
    this.userEmail = this.userInfo.email;
    this.headerText = `${this.headerText} - ${this.userEmail}`;

    const hideChangePassword = this.userInfo.features.some((feat) => feat.feature === 'ui_hide_password_change');
    if (hideChangePassword) {
      this.accountTabs = this.accountTabs.filter((section) => section.label !== AccountSections.CHANGE_PASSWORD);
    }

    const hideDeleteAccount = this.userInfo.features.some((feat) => feat.feature === 'ui_hide_delete_account');
    if (hideDeleteAccount) {
      this.accountTabs = this.accountTabs.filter((section) => section.label !== AccountSections.DELETE_ACCOUNT);
    }

    this.checkNewsletterSubscription();
    const openAccountSettingDialogBox = window.localStorage.getItem('dropbox_authorization_FlagFor_open_dailogbox');
    if (openAccountSettingDialogBox === 'openDialogBox') {
      setTimeout(() => {
        this.accountSettingSelectedTab(AccountSections.CONNECTED_APPS);
      }, 1100);
    }

    this.addValidationForUpdatePassword();
  }

  addValidationForUpdatePassword() {
    this.accountUpdateButton.disabled = this.form.invalid;
    this.form.valueChanges.subscribe((item) => {
      this.accountUpdateButton = { ...this.accountUpdateButton };
      this.confirmButton = {
        ...this.accountUpdateButton,
        disabled: this.form.invalid
      };
    });
  }

  accountSettingSelectedTab(tabname) {
    this.selectedSection = tabname;
    if (tabname === this.accountSections.CONNECTED_APPS) {
      this.getConnectedAppsStatus();
    }
    this.getConfirmButton();
  }

  get f() {
    return this.form.controls;
  }

  saveSubscription() {
    // Confirm email subscription
    const status = this.subscription ? 0 : 1;
    this.authApiService.saveSubscription(status).subscribe({
      next: () => {
        this.toastr.showMessage(status === 1 ? 'Subscribed Successfully' : 'Unsubscribed Successfully');
      },
      error: () => {}
    });
    this.closeDialog();
  }

  submit() {
    const task = this.form.value;
    // check basic conditions and update password.
    delete this.form.value.password2;
    const body = JSON.stringify(task);
    this.authApiService.updateAccountSettingPassword(body).subscribe({
      next: () => {
        this.toastr.showMessage('Password updated successfully');
        this.closeDialog();
      },
      error: () => {}
    });
  }

  checkNewsletterSubscription() {
    // confirmation Subscribe to E-mail News Letter.
    this.authApiService.checkSubscription().subscribe((res: any) => {
      this.subscription = res.status === 0;
    });
  }

  deleteAccount() {
    // Completely delete an account.
    const statusObj = {
      header: 'Deleting Account',
      text: 'Account will be deleted permanently',
      cancel_text: 'Cancel',
      delete_text: 'Delete'
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: statusObj.header,
        text: statusObj.text,
        cancel_text: statusObj.cancel_text,
        delete_text: statusObj.delete_text
      }
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.authApiService.deleteAccount().subscribe((res: any) => {
          this.toastr.showMessage('Account deleted Successfully');
          localStorage.removeItem('user_info');
          localStorage.removeItem('puser_token');
          // creating broadcast
        });
        this.closeDialog();
      }
    });
  }

  restoreProfile() {
    // Account settings -> Your Dashboard -> To show confirmation and restore dashboard settings.
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        type: 'Confirm', // Show confirm dialog with Primary Action button
        header: 'Are you sure?',
        text: 'All of the users stored preferences will be removed.',
        cancel_text: 'Cancel',
        confirm_text: 'Yes'
      }
    });
    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.closeDialog();
        this.postAttributesApi({});
      }
    });
  }

  postAttributesApi(app) {
    // api call to reset user settings data.
    const body = JSON.stringify(app);
    this.userProfileApiService.postAttributesApi(body).subscribe();
  }

  getConnectedAppsStatus() {
    this.loadingFlag = true;
    this.dataSourceApiService.getConnectedAppsStatus().subscribe((res: any) => {
      localStorage.setItem('dropbox_authorization_FlagFor_open_dailogbox', 'closeDialogBox');
      this.connectedApps = [];
      Object.entries(res).forEach(([key, value]) => {
        if (key === 'dropbox' || key === 'sharepoint') {
          this.connectedApps.push({
            appname: key,
            button: value ? 'disconnect' : 'connect',
            icon: `/assets/icons/svg-imports/${key}.svg`
          });
        }
      });
      this.loadingFlag = false;

      localStorage.removeItem('dropbox_to_makersite_page_url');
      localStorage.removeItem('dropbox_to_callbackUrl');
      localStorage.removeItem('dropbox_to_makersite_api_SERVER_URL');
      localStorage.removeItem('dropbox_or_sharepoint_from_importer');
      localStorage.removeItem('dropbox_to_api_token');
      localStorage.removeItem('sharePoint_to_callbackUrl');
      localStorage.removeItem('dropbox_authorization_FlagFor_open_dailogbox');
    });
  }

  connectAppForAuthentication() {
    // we are getting through this api drop-box auth url for backend
    // api - auth/dropbox/login
    const baseUrlOfSite = window.location.hostname;
    const callbackUrl =
      baseUrlOfSite === 'localhost'
        ? 'https://new-dev.makersite.app/dropbox-authorization.html'
        : `https://${baseUrlOfSite}/dropbox-authorization.html`;

    localStorage.setItem('dropbox_to_callbackUrl', callbackUrl);
    // set api server url
    localStorage.setItem('dropbox_to_makersite_api_SERVER_URL', '');

    // set browser page url in local storage
    localStorage.setItem('dropbox_to_makersite_page_url', window.location.href);
    localStorage.setItem('dropbox_authorization_FlagFor_open_dailogbox', 'openDialogBox');
    localStorage.setItem('dropbox_or_sharepoint_from_importer', 'closeImporterFileDialog');

    // set auth token for dropbox in local storage
    localStorage.setItem('dropbox_to_api_token', `Bearer ${localStorage.getItem('auth_token')}`);

    window.location.href = `/api/v1/auth/dropbox/login?callback=${callbackUrl}`;
  }

  connectHandler(app: string) {
    if (app === 'dropbox') {
      this.connectAppForAuthentication();
    } else if (app === 'sharepoint') {
      this.connectSharePointAppForAuthentication();
    }
  }

  disconnectHandler(app: string) {
    if (app === 'dropbox') {
      this.disConnectAppForDropBox();
    } else if (app === 'sharepoint') {
      this.disConnectAppForSharePoint();
    }
  }

  disConnectAppForDropBox() {
    this.authApiService.disconnectAuthApplication().subscribe({
      next: () => {
        this.getConnectedAppsStatus();
      },
      error: () => {}
    });
  }

  connectSharePointAppForAuthentication() {
    const baseUrlOfSite = window.location.hostname;
    const callbackUrl =
      baseUrlOfSite === 'localhost'
        ? `http://${window.location.host}/sharepoint-authorization.html`
        : `https://${baseUrlOfSite}/sharepoint-authorization.html`;

    // callbackUrl ='http://'+hostName+'/sharepoint-authorization.html';
    localStorage.setItem('sharePoint_to_callbackUrl', callbackUrl);

    // set auth token in local storage for the callback page
    localStorage.setItem('dropbox_to_api_token', `Bearer ${localStorage.getItem('auth_token')}`);

    // set api server url
    localStorage.setItem('dropbox_to_makersite_api_SERVER_URL', '');

    // set browser page url in local storage
    localStorage.setItem('dropbox_to_makersite_page_url', window.location.href);
    localStorage.setItem('dropbox_authorization_FlagFor_open_dailogbox', 'openDialogBox');

    this.dataSourceApiService.getShareAuthenticationUrl(callbackUrl).subscribe((res: any) => {
      window.location.href = res.auth_url;
    });
  }

  disConnectAppForSharePoint() {
    this.authApiService.disconnectAuthApplicationSharePoint().subscribe({
      next: () => {
        this.getConnectedAppsStatus();
      },
      error: () => {}
    });
  }
}
